import {
  ComponentProps,
  forwardRef,
  FC,
  useCallback,
  ChangeEvent,
  useState,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { noop, pxToRem } from '@gif/shared-global';
import { CloseIcon } from '../../assets';

const ICON_HEIGHT = 12;

const Wrapper = styled.div`
  display: inline-flex;
  width: 100%;
`;

const InputComponent = styled.input`
  display: inline-flex;
  padding: 0 ${pxToRem(ICON_HEIGHT)} 0 ${pxToRem(ICON_HEIGHT / 2)};
  align-items: center;
  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const IconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: -${pxToRem(ICON_HEIGHT * 1.5)};
  pointer-events: none;
`;

const CrossIcon = styled(CloseIcon)`
  height: ${pxToRem(ICON_HEIGHT)};
  cursor: pointer;
`;

export interface InputProps extends Omit<ComponentProps<'input'>, 'ref'> {
  value?: string;
  hasError?: boolean;
  SvgIcon?: FC<React.SVGProps<SVGSVGElement>>;
  showIcon?: boolean;
  onIconClick?: (e: React.MouseEvent) => void;
  enableInputReset?: boolean;
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    hasError,
    value = '',
    SvgIcon = CrossIcon,
    showIcon = false,
    onIconClick = noop,
    onChange = noop,
    enableInputReset,
    className,
    ...rest
  } = props;
  const [inputValue, setInputValue] = useState(value);
  const [shouldShowIcon, setShouldShowIcon] = useState(showIcon);
  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      if (enableInputReset) {
        setShouldShowIcon(inputValue !== '');
      }
      setInputValue(inputValue);
      onChange(event);
    },
    [onChange, enableInputReset]
  );
  const onIconClickHandler = useCallback(
    (event: React.MouseEvent) => {
      if (enableInputReset) {
        setInputValue('');
        setShouldShowIcon(false);
      }
      onIconClick(event);
    },
    [enableInputReset, onIconClick]
  );
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  useEffect(() => {
    setShouldShowIcon(showIcon);
  }, [showIcon]);
  return (
    <Wrapper className={className}>
      <InputComponent
        {...rest}
        ref={ref}
        value={inputValue}
        aria-invalid={hasError}
        onChange={onChangeHandler}
      />
      {shouldShowIcon && (
        <IconContainer>
          <SvgIcon onClick={onIconClickHandler} />
        </IconContainer>
      )}
    </Wrapper>
  );
});

export default Input;
